import { filterClasses } from "@swivl-mono/helpers";
import { ChipSelect, SelectOutlet } from "@swivl-mono/ui";
import React, { useEffect, useMemo, useState } from "react";

import { useTicketingStore } from "../../../../Models/Ticketing/Ticketing.Store";
import { TicketPropertyRow } from "./TicketPropertyRow";

type TagFieldProps = {
  tags: string[];
  onChange: (tags: string[]) => void;
  pending?: boolean;
};

const TAG_DOT_COLORS = [
  "bg-grey-900",
  "bg-orange-500",
  "bg-red-500",
  "bg-yellow-700",
  "bg-teal-500",
  "bg-purple-500",
  "bg-blue-500",
  "bg-green-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-grey-500",
  "bg-orange-700",
  "bg-red-700",
  "bg-yellow-500",
  "bg-teal-900",
  "bg-purple-700",
  "bg-blue-700",
  "bg-green-700",
  "bg-pink-700",
  "bg-indigo-700"
];

function getTagDotColor(index: number): string {
  return TAG_DOT_COLORS[index % TAG_DOT_COLORS.length];
}

export function TagField({ tags, onChange, pending }: TagFieldProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>(tags);
  const [isEditing, setIsEditing] = useState(false);

  const tagOptions = useTicketingStore((s) => s.tagOptions);
  const ensureTagsLoaded = useTicketingStore((s) => s.ensureTagsLoaded);
  const addTagOption = useTicketingStore((s) => s.addTagOption);

  useEffect(() => {
    void ensureTagsLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync local state when props change (after save or external update)
  useEffect(() => {
    // Don't clobber an in-progress edit; resync on exit.
    if (isEditing) return;
    setSelectedTags(tags);
    tags.forEach((tag) => addTagOption(tag));
  }, [tags, addTagOption, isEditing]);

  const options = useMemo(() => {
    return tagOptions.map((tag, index) => ({
      label: tag,
      value: tag,
      icon: <span className={filterClasses("inline-block size-1.5 shrink-0 rounded-full", getTagDotColor(index))} />
    }));
  }, [tagOptions]);

  const headerContent = useMemo(() => {
    if (selectedTags.length === 0) return <span>No Tag</span>;
    return selectedTags.map((tag, i) => {
      const colorIndex = tagOptions.indexOf(tag);
      const dotColor = colorIndex >= 0 ? getTagDotColor(colorIndex) : "bg-grey-500";
      return (
        <React.Fragment key={tag}>
          {i > 0 && <span>, </span>}
          <span className="inline-flex items-center gap-1">
            <span className={filterClasses("inline-block size-2 shrink-0 rounded-full", dotColor)} />
            <span>{tag}</span>
          </span>
        </React.Fragment>
      );
    });
  }, [selectedTags, tagOptions]);

  const handleSelectionChange = (selected: string[]): void => {
    setIsEditing(true);
    setSelectedTags(selected);
  };

  const handleDone = (): void => {
    setIsEditing(false);
    onChange(selectedTags);
  };

  const handleOpenChange = (open: boolean): void => {
    // Closing without Accept cancels the draft; let the resync catch up.
    if (!open) setIsEditing(false);
  };

  const handleCreateTag = (tagName: string): void => {
    setIsEditing(true);
    addTagOption(tagName);
    setSelectedTags((prev) => (prev.includes(tagName) ? prev : [...prev, tagName]));
  };

  return (
    <TicketPropertyRow label="Tag" pending={pending}>
      <ChipSelect
        styles={{ [SelectOutlet.POPOVER]: { minWidth: 240 } }}
        hasSearchInDropdown
        placeholder="No Tag"
        options={options}
        selectedValues={selectedTags}
        onChange={handleSelectionChange}
        onAccept={handleDone}
        onOpenChange={handleOpenChange}
      />
    </TicketPropertyRow>
  );
}
